import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/FirebaseAuthContext';
import { useFirebaseCredits } from '../hooks/useFirebaseCredits';
import { useSubscription } from '../context/SubscriptionContext';

export const SubscriptionBanner: React.FC = () => {
  const { user } = useAuth();
  const { credits, loading, hasUnlimitedCredits } = useFirebaseCredits();
  const { isSubscribed } = useSubscription();

  // Don't show for premium users, or before credits have loaded (avoids a false warning flash)
  if (hasUnlimitedCredits || isSubscribed || loading) return null;

  // Don't show trial banner for guests
  if (!user) return null;

  // ALARM if credits ≤ 3
  if (typeof credits === 'number' && credits <= 3) {
    // Dynamic styling based on urgency
    const urgencyLevel = credits === 0 ? 'critical' : credits === 1 ? 'urgent' : 'warning';

    const styles = {
      critical: {
        bg: 'bg-gradient-to-r from-red-600 via-rose-600 to-pink-600',
        icon: '🚨',
        iconAnim: 'animate-pulse',
        message: 'OUT OF CREDITS!',
        buttonBg: 'bg-white hover:bg-gray-50',
        buttonText: 'text-red-600',
      },
      urgent: {
        bg: 'bg-gradient-to-r from-orange-600 via-amber-600 to-yellow-600',
        icon: '⚡',
        iconAnim: 'animate-bounce',
        message: 'LAST CREDIT!',
        buttonBg: 'bg-white hover:bg-gray-50',
        buttonText: 'text-orange-600',
      },
      warning: {
        bg: 'bg-gradient-to-r from-amber-500 via-orange-500 to-yellow-500',
        icon: '⚠️',
        iconAnim: 'animate-pulse',
        message: `Only ${credits} credits left!`,
        buttonBg: 'bg-white hover:bg-gray-50',
        buttonText: 'text-amber-600',
      }
    };

    const style = styles[urgencyLevel];

    return (
      <div className="sticky top-0 z-50 px-3 sm:px-4 py-3">
        <div className={`${style.bg} text-white rounded-2xl shadow-xl border-2 border-white/30 backdrop-blur-sm max-w-7xl mx-auto`}>
          <div className="px-3 sm:px-6 py-3 sm:py-4">
            <div className="flex items-center justify-between gap-3 sm:gap-4">
              {/* Left: Warning Message */}
              <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                <span className={`text-xl sm:text-2xl ${style.iconAnim} flex-shrink-0`}>
                  {style.icon}
                </span>
                <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2 min-w-0">
                  <span className="font-bold text-sm sm:text-base whitespace-nowrap">
                    {style.message}
                  </span>
                  <span className="text-xs sm:text-sm opacity-90 hidden sm:inline">
                    Get unlimited access now
                  </span>
                </div>
              </div>

              {/* Right: CTA Button */}
              <Link
                to="/pricing"
                className={`${style.buttonBg} ${style.buttonText} px-3 sm:px-6 py-1.5 sm:py-2 rounded-xl font-bold transition-all hover:scale-105 shadow-lg flex items-center gap-1.5 sm:gap-2 whitespace-nowrap text-xs sm:text-sm flex-shrink-0`}
              >
                <span className="hidden sm:inline">🎁</span>
                <span className="hidden sm:inline">Try 7 Days Free</span>
                <span className="sm:hidden">Get Free Trial</span>
                <span className="text-base sm:text-lg">→</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Soft reminder for regular users
  return (
    <div className="sticky top-0 z-50 px-3 sm:px-4 py-3">
      <div className="bg-blue-50 text-blue-800 rounded-2xl shadow-sm border-2 border-blue-200 backdrop-blur-sm max-w-7xl mx-auto px-4 py-2.5 text-center text-xs sm:text-sm">
        💡 Tip: Try{' '}
        <Link to="/pricing" className="underline font-bold hover:text-blue-600">
          7 days free trial
        </Link>
        {' '}for unlimited usage
      </div>
    </div>
  );
}; 